import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    useMediaQuery, useTheme, TextField, Chip, Button, FormControl, InputLabel, Select, MenuItem,
    CircularProgress, TablePagination, Dialog, DialogTitle, DialogContent, DialogActions,
    Alert, Checkbox, Tooltip, IconButton, Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InventoryIcon from '@mui/icons-material/Inventory';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';

import api from '../../services/api';
import { isVisualizador } from '../../services/auth';
import { toastSuccess, alertValidacao } from '../../services/alerts';
import {
    STATUS_LOTE, labelStatusLote, corStatusLote,
    formatarMoeda, formatarData, competenciaDoMes,
} from '../../services/convenios';

export default function Lotes() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const soLeitura = isVisualizador();

    const [lotes, setLotes] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const [convenios, setConvenios] = useState([]);
    const [filtroConvenio, setFiltroConvenio] = useState('');
    const [filtroStatus, setFiltroStatus] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const [dialogAberto, setDialogAberto] = useState(false);
    const [novo, setNovo] = useState({ idPlanoSaude: '', ...competenciaDoMes(), observacoes: '' });
    const [faturaveis, setFaturaveis] = useState([]);
    const [selecionadas, setSelecionadas] = useState([]);
    const [buscandoFaturaveis, setBuscandoFaturaveis] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get('/planos-saude').then(r => setConvenios(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }, []);

    const hasActiveFilters = !!(filtroConvenio || filtroStatus);

    const carregarDados = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, size: rowsPerPage };
            if (filtroConvenio) params.idPlanoSaude = filtroConvenio;
            if (filtroStatus) params.status = filtroStatus;
            const res = await api.get('/lotes-faturamento', { params });
            setLotes(res.data?.content ?? []);
            setTotalElements(res.data?.totalElements ?? 0);
        } catch {
            /* erro exibido pelo handler global */
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, filtroConvenio, filtroStatus]);

    useEffect(() => { carregarDados(); }, [carregarDados]);
    useEffect(() => { setPage(0); }, [filtroConvenio, filtroStatus]);

    const abrirDialog = () => {
        setNovo({ idPlanoSaude: '', ...competenciaDoMes(), observacoes: '' });
        setFaturaveis([]);
        setSelecionadas([]);
        setDialogAberto(true);
    };

    /** Prévia do que entra no lote: as guias EMITIDAS do período ainda sem lote. */
    const buscarFaturaveis = useCallback(async () => {
        if (!novo.idPlanoSaude || !novo.inicio || !novo.fim) { setFaturaveis([]); return; }
        setBuscandoFaturaveis(true);
        try {
            const { data } = await api.get('/guias/faturaveis', {
                params: { idPlanoSaude: novo.idPlanoSaude, inicio: novo.inicio, fim: novo.fim },
                skipGlobalError: true,
            });
            const lista = Array.isArray(data) ? data : [];
            setFaturaveis(lista);
            setSelecionadas(lista.map(g => g.id_guia_atendimento));
        } catch {
            setFaturaveis([]);
            setSelecionadas([]);
        } finally {
            setBuscandoFaturaveis(false);
        }
    }, [novo.idPlanoSaude, novo.inicio, novo.fim]);

    useEffect(() => { if (dialogAberto) buscarFaturaveis(); }, [dialogAberto, buscarFaturaveis]);

    const alternarSelecao = (id) => setSelecionadas(prev =>
        prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const totalSelecionado = faturaveis
        .filter(g => selecionadas.includes(g.id_guia_atendimento))
        .reduce((soma, g) => soma + Number(g.valorTotal || 0), 0);

    const semNumero = faturaveis
        .filter(g => selecionadas.includes(g.id_guia_atendimento) && !g.numeroGuia).length;

    const handleCriar = async () => {
        if (!novo.idPlanoSaude) { alertValidacao('Selecione o convênio.'); return; }
        if (!novo.inicio || !novo.fim) { alertValidacao('Informe a competência do lote.'); return; }
        if (novo.fim < novo.inicio) { alertValidacao('Fim da competência anterior ao início.'); return; }
        if (!selecionadas.length) { alertValidacao('Selecione ao menos uma guia.'); return; }

        setSaving(true);
        try {
            const { data } = await api.post('/lotes-faturamento', {
                idPlanoSaude: Number(novo.idPlanoSaude),
                competenciaInicio: novo.inicio,
                competenciaFim: novo.fim,
                // Manda sempre a lista escolhida: assim desmarcar uma guia na
                // prévia vale, em vez de o back puxar todas de novo.
                idsGuias: selecionadas,
                observacoes: novo.observacoes || null,
            });
            toastSuccess('Lote criado');
            setDialogAberto(false);
            navigate(`/lotes-faturamento/${data.id_lote_faturamento}`);
        } catch {
            /* erro exibido pelo handler global */
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
                <Typography variant={isMobile ? 'h5' : 'h4'} component="div" sx={{ fontWeight: 'bold', color: '#333' }}>
                    Lotes de faturamento
                </Typography>
                {!soLeitura && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={abrirDialog}>
                        Novo lote
                    </Button>
                )}
            </Box>

            <Paper elevation={0} sx={{ p: isMobile ? 1.5 : 3, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                        {totalElements} lote(s){hasActiveFilters && ' • Filtros aplicados'}
                    </Typography>
                    <IconButton
                        onClick={() => setShowFilters(!showFilters)}
                        sx={{
                            border: '1px solid',
                            borderColor: showFilters || hasActiveFilters ? 'primary.main' : '#e0e0e0',
                            borderRadius: 2, p: 1.2,
                            color: showFilters || hasActiveFilters ? 'primary.main' : 'text.secondary',
                        }}
                    >
                        {showFilters ? <FilterListOffIcon /> : <FilterListIcon />}
                    </IconButton>
                </Box>

                <Collapse in={showFilters}>
                    <Box sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px dashed #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Convênio</InputLabel>
                            <Select value={filtroConvenio} label="Convênio" onChange={(e) => setFiltroConvenio(e.target.value)} sx={{ bgcolor: '#fff' }}>
                                <MenuItem value="">Todos</MenuItem>
                                {convenios.map(c => <MenuItem key={c.id_plano_saude} value={c.id_plano_saude}>{c.nome}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Situação</InputLabel>
                            <Select value={filtroStatus} label="Situação" onChange={(e) => setFiltroStatus(e.target.value)} sx={{ bgcolor: '#fff' }}>
                                <MenuItem value="">Todas</MenuItem>
                                {Object.keys(STATUS_LOTE).map(k => (
                                    <MenuItem key={k} value={k}>{STATUS_LOTE[k].label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {hasActiveFilters && (
                            <Button variant="text" color="error" size="small"
                                onClick={() => { setFiltroConvenio(''); setFiltroStatus(''); }} sx={{ fontWeight: 'bold' }}>
                                Limpar filtros
                            </Button>
                        )}
                    </Box>
                </Collapse>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress sx={{ color: '#1ABB9C' }} />
                    </Box>
                ) : (
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size={isMobile ? 'small' : 'medium'}>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell><strong>Lote</strong></TableCell>
                                    <TableCell><strong>Convênio</strong></TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Competência</strong></TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Envio</strong></TableCell>
                                    <TableCell align="center"><strong>Guias</strong></TableCell>
                                    <TableCell align="right"><strong>Valor</strong></TableCell>
                                    <TableCell align="right" sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Glosado</strong></TableCell>
                                    <TableCell><strong>Situação</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lotes.length > 0 ? lotes.map((l) => (
                                    <TableRow
                                        key={l.id_lote_faturamento}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/lotes-faturamento/${l.id_lote_faturamento}`)}
                                    >
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <InventoryIcon fontSize="small" color="action" />
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        #{l.id_lote_faturamento}
                                                    </Typography>
                                                    {l.protocolo && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            Protocolo {l.protocolo}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{l.nomePlanoSaude}</TableCell>
                                        <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                            {formatarData(l.competenciaInicio)} — {formatarData(l.competenciaFim)}
                                        </TableCell>
                                        <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                            {l.dataEnvio ? formatarData(l.dataEnvio) : '-'}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip size="small" variant="outlined" label={l.quantidadeGuias} />
                                            {l.guiasAguardandoRetorno > 0 && (
                                                <Tooltip title="Guias sem retorno da operadora">
                                                    <Chip size="small" color="warning" sx={{ ml: 0.5 }}
                                                        label={`${l.guiasAguardandoRetorno} pend.`} />
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">{formatarMoeda(l.valorTotal)}</TableCell>
                                        <TableCell align="right" sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                            {formatarMoeda(l.valorGlosado)}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={STATUS_LOTE[l.status]?.ajuda || ''}>
                                                <Chip size="small" label={labelStatusLote(l.status)} color={corStatusLote(l.status)} />
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" sx={{ color: '#999' }}>
                                                Nenhum lote encontrado.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    labelRowsPerPage="Linhas por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
            </Paper>

            <Dialog open={dialogAberto} onClose={() => setDialogAberto(false)} fullWidth maxWidth="md">
                <DialogTitle>Novo lote de faturamento</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <TextField select sx={{ flex: 2, minWidth: 200 }} label="Convênio *"
                                value={novo.idPlanoSaude}
                                onChange={(e) => setNovo(p => ({ ...p, idPlanoSaude: e.target.value }))}>
                                {convenios.filter(c => c.ativo).map(c => (
                                    <MenuItem key={c.id_plano_saude} value={c.id_plano_saude}>{c.nome}</MenuItem>
                                ))}
                            </TextField>
                            <TextField sx={{ flex: 1, minWidth: 150 }} type="date" label="Competência início *"
                                InputLabelProps={{ shrink: true }} value={novo.inicio}
                                onChange={(e) => setNovo(p => ({ ...p, inicio: e.target.value }))} />
                            <TextField sx={{ flex: 1, minWidth: 150 }} type="date" label="Competência fim *"
                                InputLabelProps={{ shrink: true }} value={novo.fim}
                                onChange={(e) => setNovo(p => ({ ...p, fim: e.target.value }))} />
                        </Box>

                        <TextField label="Observações" value={novo.observacoes}
                            onChange={(e) => setNovo(p => ({ ...p, observacoes: e.target.value }))}
                            inputProps={{ maxLength: 255 }} />

                        <Typography variant="subtitle2" sx={{ mt: 1 }}>
                            Guias disponíveis ({faturaveis.length})
                        </Typography>

                        {buscandoFaturaveis ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={24} sx={{ color: '#1ABB9C' }} />
                            </Box>
                        ) : faturaveis.length === 0 ? (
                            <Alert severity="info">
                                Nenhuma guia emitida e sem lote neste convênio no período. Emita guias dos
                                atendimentos antes de montar o lote.
                            </Alert>
                        ) : (
                            <>
                                <TableContainer sx={{ maxHeight: 300, overflowX: 'auto' }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox" />
                                                <TableCell><strong>Guia</strong></TableCell>
                                                <TableCell><strong>Paciente</strong></TableCell>
                                                <TableCell><strong>Atendimento</strong></TableCell>
                                                <TableCell align="right"><strong>Valor</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {faturaveis.map(g => (
                                                <TableRow key={g.id_guia_atendimento} hover>
                                                    <TableCell padding="checkbox">
                                                        <Checkbox size="small"
                                                            checked={selecionadas.includes(g.id_guia_atendimento)}
                                                            onChange={() => alternarSelecao(g.id_guia_atendimento)} />
                                                    </TableCell>
                                                    <TableCell>
                                                        {g.numeroGuia || `#${g.id_guia_atendimento}`}
                                                        {!g.numeroGuia && (
                                                            <Chip size="small" color="warning" sx={{ ml: 1 }} label="sem número" />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>{g.nomePaciente}</TableCell>
                                                    <TableCell>{formatarData(g.dataAtendimento)}</TableCell>
                                                    <TableCell align="right">{formatarMoeda(g.valorTotal)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {selecionadas.length} guia(s) selecionada(s)
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        {formatarMoeda(totalSelecionado)}
                                    </Typography>
                                </Box>

                                {semNumero > 0 && (
                                    <Alert severity="warning">
                                        {semNumero} guia(s) sem número da operadora. O lote pode ser montado, mas o
                                        envio só é aceito depois que todas tiverem número.
                                    </Alert>
                                )}
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogAberto(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleCriar} disabled={saving || !selecionadas.length}>
                        {saving ? 'Criando...' : 'Criar lote'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
